import {
  GameWeek,
  NFL_TEAMS_TOTAL,
  NFL_WEEKS_TOTAL,
  TeamAbrvToTeamID,
  TeamNamesByID,
  TID_LIST,
} from '@shared/constants';
import {
  breakUID,
  getGameWeekKeyByDateString,
  getGameWeekKeyByGameID,
  getSeasonIdFromGameID,
  to4CharTeamID,
  toSeasonWeekID,
  toUID,
} from '@shared/helpers';
import {
  GameID,
  IEnhancedTeamStats,
  IGameStats,
  ITeamDetails,
  ITeamsOnByeWeek,
  SeasonWeekID,
  TeamID,
  TID,
  UID,
} from '@shared/types';
import { sortByWeekID } from './SortHelpers';

export const toSeasonWeekIDByGameID = (gameID: string): SeasonWeekID => {
  const seasonID = getSeasonIdFromGameID(gameID);
  const weekID = getGameWeekKeyByGameID(gameID);
  return toSeasonWeekID(seasonID, weekID);
};

// #region TEAM ID HELPER FUNCTIONS *******************************************
export const toTIDByTeamID = (teamID: TeamID) => {
  const teamAbrv = TeamNamesByID[parseInt(teamID, 10)];
  return toTID(teamAbrv, teamID);
};
export const toTIDByAbrv = (teamAbrv: string) => {
  const index = TeamNamesByID.indexOf(teamAbrv);
  return toTID(teamAbrv, String(index));
};
export const breakTID = (tID: TID) => {
  const [team4CharID, teamAbrv] = tID.split('-');
  return { team4CharID, teamAbrv, teamNumID: parseInt(team4CharID) };
};
const isValidTID = (value: string): value is TID => {
  return TID_LIST.includes(value as TID);
};
/**
 * Converts team abbreviation and team ID into a TID.
 *
 * @param {string} teamAbrv - #team abbreviation
 * @param {string} teamID   - team ID
 *
 * @return {string} The TID in the format "XXXX-TEAMABRV",
 * where "XXXX" is the 4-character zero team ID,
 * and TEAMABRV is abbreviated team name
 * e.g. 0001-ARI, 0002-ATL, 0032-WSH
 */
export const toTID = (teamAbrv: string, teamID: TeamID): TID => {
  const tID = `${to4CharTeamID(teamID)}-${teamAbrv}`;
  return isValidTID(tID) ? tID : TID_LIST[Math.max(parseInt(teamID) - 1, 0)];
};
// #endregion

export const generateTeamStatsPayload = (gameStats: IGameStats, isHomeTeam: boolean): IEnhancedTeamStats => {
  const teamID = isHomeTeam ? gameStats.teamIDHome : gameStats.teamIDAway;
  const gameID = gameStats.gameID;
  const weekID = getGameWeekKeyByDateString(gameStats.gameDate);
  const uID: UID = toUID(weekID, teamID, gameStats.gameID);
  const seasonWeekID = toSeasonWeekID(breakUID(uID).yearID, weekID);
  const teamStats = isHomeTeam ? gameStats.teamStats.home : gameStats.teamStats.away;
  const pointsScored = isHomeTeam ? parseInt(gameStats.homePts) : parseInt(gameStats.awayPts);
  const teamDSTStats = isHomeTeam
    ? {
        ...gameStats.DST.home,
        passingYardsAllowed: gameStats.teamStats.away.passingYards,
        rushingYardsAllowed: gameStats.teamStats.away.rushingYards,
      }
    : {
        ...gameStats.DST.away,
        passingYardsAllowed: gameStats.teamStats.home.passingYards,
        rushingYardsAllowed: gameStats.teamStats.home.rushingYards,
      };

  return { ...teamStats, gameID, weekID, pointsScored, ...teamDSTStats, teamID, uID, seasonWeekID };
};

export const getTeamStatsFromGameStats = (gameStats: IGameStats): IEnhancedTeamStats[] => {
  const weekID = getGameWeekKeyByDateString(gameStats.gameDate);
  const homeUID: UID = toUID(weekID, gameStats.teamIDHome, gameStats.gameID);
  const awayUID: UID = toUID(weekID, gameStats.teamIDAway, gameStats.gameID);
  const seasonWeekID = toSeasonWeekID(breakUID(homeUID).yearID, weekID);

  const homeTeamStats: IEnhancedTeamStats = {
    uID: homeUID,
    ...gameStats.teamStats.home,
    gameID: gameStats.gameID,
    weekID,
    seasonWeekID,
    pointsScored: parseInt(gameStats.homePts),
    ...gameStats.DST.home,
    passingYardsAllowed: gameStats.teamStats.away.passingYards,
    rushingYardsAllowed: gameStats.teamStats.away.rushingYards,
    teamID: gameStats.teamIDHome,
  };

  const awayTeamStats: IEnhancedTeamStats = {
    uID: awayUID,
    ...gameStats.teamStats.away,
    gameID: gameStats.gameID,
    weekID,
    seasonWeekID,
    pointsScored: parseInt(gameStats.awayPts),
    ...gameStats.DST.away,
    passingYardsAllowed: gameStats.teamStats.home.passingYards,
    rushingYardsAllowed: gameStats.teamStats.home.rushingYards,
    teamID: gameStats.teamIDAway,
  };

  return [homeTeamStats, awayTeamStats];
};

export const getTeamIDsFromGameID = (gameID: GameID): [string, string] => {
  const [away, home] = gameID.split('_')[1].split('@');
  return [
    TeamAbrvToTeamID[home as keyof typeof TeamAbrvToTeamID],
    TeamAbrvToTeamID[away as keyof typeof TeamAbrvToTeamID],
  ];
};

export const getTeamNamesFromGameID = (gameID: string): [string, string] => {
  const [away, home] = gameID.split('_')[1].split('@');
  return [home, away];
};

export type DefaultWeeklyListType = Array<GameWeek>;

// export const getDefaultWeeklyStats = (defaultValue )

// #region TEAM RESULTS REPORT
export type TeamResultsReportT = {
  numberOfFullWeeks: number;
  numberOfMissingTeams: number;
  completeWeeks: GameWeek[];
  incompleteWeeks: GameWeek[];
  teamResultsInIncompleteWeeks: IEnhancedTeamStats[][];
  isIncompleteWeeksMatched: boolean;
  missingTeams: string[];
  missingTeamsGameIDs?: string[];
  missingTeamsGameStats?: IGameStats[];
  isAllMissingTeamsInGameStats?: boolean;
};

export type AvailableWeeksInReduxPayload = Pick<TeamResultsReportT, 'numberOfFullWeeks' | 'numberOfMissingTeams'>;
/**
 * Returns the number of weeks available in the Redux store, number of missing teams
 *
 * @param { IEnhancedTeamStats[]} reduxData - Redux store data
 * @returns {AvailableWeeksInReduxPayload} - Number of weeks and missing teams
 */
export const getAvailableWeeksInRedux = (reduxData: IEnhancedTeamStats[]): AvailableWeeksInReduxPayload => {
  if (!!reduxData && reduxData.length > 0) {
    const reduxLength = reduxData.length;
    // 32 teams per week, so divide by 32 to get number of weeks
    const weeks = Math.floor(reduxLength / 32);
    const remainder = reduxLength % 32;
    return {
      numberOfFullWeeks: weeks,
      numberOfMissingTeams: remainder ? 32 - remainder : remainder,
    };
  }

  return {
    numberOfFullWeeks: 0,
    numberOfMissingTeams: 0,
  };
};

export type CompleteAndIncompleteTeamResultWeeksPayload = Pick<
  TeamResultsReportT,
  'completeWeeks' | 'incompleteWeeks' | 'teamResultsInIncompleteWeeks' | 'isIncompleteWeeksMatched'
>;

export const getCompleteAndIncompleteTeamResultWeeks = (
  numberOfFullWeeks: number,
  rdxStoreData: IEnhancedTeamStats[]
): CompleteAndIncompleteTeamResultWeeksPayload => {
  // if there are any remaining teams, calculate the number of missing teams
  const weeksList = Object.keys(GameWeek) as GameWeek[];
  const incompleteWeeks: GameWeek[] = [];
  const completeWeeks: GameWeek[] = [];
  const teamResultsInIncompleteWeeks: IEnhancedTeamStats[][] = [];

  // determine which week has missing teams
  for (let i = 0; i < numberOfFullWeeks + 1; i++) {
    const weekID = weeksList[i];
    const weekOfTeams = rdxStoreData.filter((team) => team.weekID === weekID);
    if (weekOfTeams.length % 32 !== 0) {
      teamResultsInIncompleteWeeks.push(weekOfTeams);
      incompleteWeeks.push(weekID);
      console.log(`Week ${weekID} has missing teams`);
    } else {
      completeWeeks.push(weekID);
    }
  }

  const isIncompleteWeeksMatched =
    incompleteWeeks.length === teamResultsInIncompleteWeeks.length && incompleteWeeks.length > 0;

  return {
    completeWeeks,
    incompleteWeeks,
    teamResultsInIncompleteWeeks,
    isIncompleteWeeksMatched,
  };
};

export type GameResultsForMissingTeamsPayload = Pick<
  TeamResultsReportT,
  'missingTeams' | 'missingTeamsGameIDs' | 'missingTeamsGameStats' | 'isAllMissingTeamsInGameStats'
>;

export const getMissingTeamsGameResults = (
  weeksWithTeamResults: CompleteAndIncompleteTeamResultWeeksPayload,
  gameStats: IGameStats[]
): GameResultsForMissingTeamsPayload => {
  const missingTeams: string[] = [];
  const missingTeamAbrvs: string[] = [];

  if (weeksWithTeamResults.isIncompleteWeeksMatched) {
    weeksWithTeamResults.teamResultsInIncompleteWeeks.forEach((teams, index) => {
      let allTeams = TeamNamesByID.slice(1);
      teams.forEach((team) => {
        allTeams = allTeams.filter((t) => t !== team.teamAbv);
      });
      missingTeamAbrvs.push(...allTeams);
      missingTeams.push(`${weeksWithTeamResults.incompleteWeeks[index]}:${allTeams.join(', ')}`);
    });
    // setMissingTeams(missingTeams);
  }

  // find out if maybe we have gameResults for meeting teams
  // returns all available games as `IGameStats[]` in a bad week
  const availableGamesInBadWeek: IGameStats[] = gameStats.filter((game) =>
    weeksWithTeamResults.incompleteWeeks.includes(getGameWeekKeyByGameID(game.gameID) as GameWeek)
  );

  const isGameIDWithMissingGame = (gameID: string) =>
    missingTeamAbrvs.find((teamAbrv) => gameID.indexOf(teamAbrv) !== -1);
  const missingTeamsGameStats = availableGamesInBadWeek.filter((game) => isGameIDWithMissingGame(game.gameID));
  const missingTeamsGameIDs = missingTeamsGameStats.map((game) => game.gameID);
  const isAllMissingTeamsInGameStats = missingTeamsGameIDs.length * 2 === missingTeamAbrvs.length;

  return {
    missingTeams,
    missingTeamsGameIDs,
    missingTeamsGameStats,
    isAllMissingTeamsInGameStats,
  };
};

export const getTeamResultsReport = (
  reduxStoreData: IEnhancedTeamStats[],
  gameStats: IGameStats[]
): TeamResultsReportT => {
  if (reduxStoreData.length > 0) {
    const availableWeeks = getAvailableWeeksInRedux(reduxStoreData);
    const weeksWithTeamResults = getCompleteAndIncompleteTeamResultWeeks(
      availableWeeks.numberOfFullWeeks,
      reduxStoreData
    );
    const missingTeamsGameResults = getMissingTeamsGameResults(weeksWithTeamResults, gameStats);

    return {
      ...availableWeeks,
      ...weeksWithTeamResults,
      ...missingTeamsGameResults,
    };
  }
  return {
    numberOfFullWeeks: 0,
    numberOfMissingTeams: 0,
    completeWeeks: [],
    incompleteWeeks: [],
    teamResultsInIncompleteWeeks: [],
    isIncompleteWeeksMatched: false,
    missingTeams: [],
    missingTeamsGameIDs: [],
    missingTeamsGameStats: [],
  };
};
// #endregion

// #region TEAM DETAILS REPORT (aka TEAM INFO)
export type TeamDetailsReportT = {
  numberOfAvailableTeams: number;
  isByeWeekScheduleExistsForEachTeam: boolean;
};

export const getTeamDetailsReport = (reduxStoreData: ITeamDetails[]): TeamDetailsReportT => {
  if (reduxStoreData.length > 0) {
    const numberOfAvailableTeams = reduxStoreData.length;
    const isByeWeekScheduleExistsForEachTeam = reduxStoreData.every((team) => team.schedule.length === NFL_WEEKS_TOTAL);

    return {
      numberOfAvailableTeams,
      isByeWeekScheduleExistsForEachTeam,
    };
  }
  return {
    numberOfAvailableTeams: 0,
    isByeWeekScheduleExistsForEachTeam: false,
  };
};

// #endregion

// #region TEAM BYE WEEK REPORT (aka TEAM INFO)
export type TeamByeWeekReportT = {
  numberOfAvailableTeams: number;
  isValidNumberOfTeams: boolean;
  isValidNumberOfWeeks: boolean;
  numberOfWeeks: number;
  weeksWithTeamsOnByeWeek: ITeamsOnByeWeek[];
};

export const getByeWeekTeamsReport = (reduxStoreData: ITeamsOnByeWeek[]): TeamByeWeekReportT => {
  if (reduxStoreData.length > 0) {
    const numberOfAvailableTeams = reduxStoreData.reduce((sum, item) => sum + item.teamIDs.length, 0);
    const numberOfWeeks = reduxStoreData.length;
    const weeksWithTeamsOnByeWeek = reduxStoreData.filter((week) => week.teamIDs.length > 0).sort(sortByWeekID);

    return {
      numberOfAvailableTeams,
      numberOfWeeks,
      weeksWithTeamsOnByeWeek,
      isValidNumberOfTeams: numberOfAvailableTeams === NFL_TEAMS_TOTAL,
      isValidNumberOfWeeks: numberOfWeeks === NFL_WEEKS_TOTAL,
    };
  }
  return {
    numberOfAvailableTeams: 0,
    numberOfWeeks: 0,
    weeksWithTeamsOnByeWeek: [],
    isValidNumberOfTeams: false,
    isValidNumberOfWeeks: false,
  };
};

// #endregion
